import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/** Generic loader: { status: 'loading' | 'success' | 'empty' | 'error', data, retry } */
const useLoader = (loader, deps = []) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null });
    loader()
      .then((data) => {
        if (cancelled) return;
        const empty = Array.isArray(data) ? data.length === 0 : data == null;
        setState({ status: empty ? "empty" : "success", data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", data: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, ...deps]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
};

/**
 * Listing count per city from the public endpoint POST /all/property/count.
 * Resolves with only the cities that have at least one listing.
 */
export const useCityCounts = (cityNames) =>
  useLoader(async () => {
    const results = await Promise.allSettled(
      cityNames.map((city) => axios.post(`${BASE_URL}/all/property/count`, { city }))
    );
    if (results.every((r) => r.status === "rejected")) throw new Error("Counts unavailable");
    const counts = {};
    results.forEach((r, i) => {
      const count = r.status === "fulfilled" ? Number(r.value.data?.propertyCount) || 0 : 0;
      if (count > 0) counts[cityNames[i]] = count;
    });
    return Object.keys(counts).length ? counts : [];
  }, [cityNames.join("|")]);

/** Total published listings (public endpoint POST /all/property/noAuth/list/all, totalRecord). */
export const useListingTotal = () =>
  useLoader(async () => {
    const { data } = await axios.post(`${BASE_URL}/all/property/noAuth/list/all`, {
      page_number: 1,
      page_size: 1,
      order_by: "DESC",
    });
    const total = Number(data?.totalRecord);
    return data?.statusType === "SUCCESS" && total > 0 ? total : null;
  });
